import { Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';

/**
 * Entra tenant ID. Same across dev/staging/prod — it's Symbia's tenant.
 */
const TENANT_ID = 'e714198c-8087-41c1-9774-8513a876eea2';

/**
 * Web client app's Application (client) ID. This is the SPA registration
 * that users sign into. Different from the API client ID — that one is
 * the resource being called, this one is the caller.
 *
 * Set via VITE_WEB_CLIENT_ID at build time. Falls back to a placeholder
 * so a missing config fails loudly with a clear error rather than
 * silently passing the wrong value to Entra.
 */
const WEB_CLIENT_ID = import.meta.env.VITE_WEB_CLIENT_ID ?? 'MISSING_VITE_WEB_CLIENT_ID';

/**
 * API client ID. Used as the audience for the access tokens we request.
 */
const API_CLIENT_ID = import.meta.env.VITE_API_CLIENT_ID ?? '9723ec62-b097-42e1-b598-4d47b53d7541';

/**
 * Scope we request when acquiring tokens. The Entra app registration must
 * expose this scope on the API app, and the web app must have permission
 * to request it.
 */
export const API_SCOPE = `api://9723ec62-b097-42e1-b598-4d47b53d7541/appointments.access`;

export const msalConfig: Configuration = {
    auth: {
        clientId: WEB_CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: true,
    },
    cache: {
        // sessionStorage so tokens don't persist across browser restarts.
        // For a warehouse-staff app that's the right tradeoff: shared
        // computers shouldn't leave a logged-in session on the desktop.
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) return;
                switch (level) {
                    case LogLevel.Error:
                        console.error('[msal]', message);
                        break;
                    case LogLevel.Warning:
                        console.warn('[msal]', message);
                        break;
                    case LogLevel.Info:
                        // Quiet by default — uncomment if debugging
                        break;
                }
            },
            logLevel: LogLevel.Warning,
        },
    },
};

export const loginRequest = {
    scopes: [API_SCOPE],
};

/**
 * Singleton MSAL instance. Created once, shared across the app via
 * <MsalProvider instance={msalInstance}>.
 *
 * Must call initializeMsal() before rendering anything that depends on
 * MSAL state — see main.tsx.
 */
export const msalInstance = new PublicClientApplication(msalConfig);

let initialized = false;

export async function initializeMsal(): Promise<void> {
    if (initialized) return;
    await msalInstance.initialize();
    // If the page loaded as a result of a redirect (login or token acquisition),
    // process the response here. Returns null in normal navigation.
    await msalInstance.handleRedirectPromise().catch((err) => {
        console.error('[msal] handleRedirectPromise failed:', err);
    });
    initialized = true;

    if (import.meta.env.DEV) {
        console.info('[msal] Web client ID:', WEB_CLIENT_ID);
        console.info('[msal] API client ID:', API_CLIENT_ID);
    }
}
